<template>
  <div
    :class="[
      'c-input-raw',
      {
        '-jumbo': jumbo,
        '-has-icon': icon,
        '-not-empty': notEmpty,
        '-textarea': textArea,
        '-validation': error,
        '-has-under-input-slot': !!$slots['under-input']
      }
    ]"
    :style="style"
  >
    <c-icon
      v-if="icon"
      :icon="icon"
      :style="{ stroke: iconColor }"
      class="left-icon"
    />

    <c-input-abstract
      v-bind="$attrs"
      ref="input"
      :disabled="disabled"
      :text-area="textArea"
      :class="classes"
      @update:modelValue="$emit('update:modelValue', $event)"
      @keyup="handleInput($event.target.value)"
      v-on="componentListeners"
    >
      <slot name="under-input" slot="under-input" />
    </c-input-abstract>

    <c-typo
      v-if="showCharacterCount && textArea"
      opacity="base-30"
      class="character-count"
    >
      {{ currentLength }}/{{ currentMaxLength() }}
    </c-typo>

    <c-icon
      v-if="rightIcon && !(validation && jumbo)"
      :icon="rightIcon"
      :style="{ stroke: iconColor }"
      class="right-icon"
    />
  </div>
</template>

<script>
import { MediaQuery } from '@convenia/mixins'
import useVue3Migration from '@convenia/composables/useVue3Migration'
import CIcon from '@convenia/components/CIcon'
import CInputAbstract from '@convenia/components/CInputAbstract'
import CTypo from '@convenia/components/CTypo'

/**
 * Our main input component.
 */
export default {
  name: 'CInputRaw',

  mixins: [ MediaQuery ],

  components: {
    CIcon,
    CInputAbstract,
    CTypo
  },

  emits: [
    'input',
    'update:modelValue'
  ],

  props: {

    /**
     * The icon to show on the left-side.
     */
    icon: {
      type: String,
      default: ''
    },

    /**
     * The icon to show in the right-side.
     */
    rightIcon: {
      type: String,
      default: ''
    },

    /**
     * The color of the icon(s).
     */
    iconColor: {
      type: String,
      default: null,
    },

    /**
     * Wheter the input has border-radius.
     */
    round: Boolean,

    /**
     * Wheter the input is opaque
     */
    opaque: Boolean,

    /**
     * Removes hover effect.
     */
    noHover: Boolean,

    /**
     * Disables the input.
     */
    disabled: Boolean,

    /**
     * Whether it is a textarea.
     */
    textArea: Boolean,

    /**
     * Jumbo style.
     */
    jumbo: Boolean,

    /**
     * Horizontal padding (in px)
     */
    horizontalPadding: {
      type: [ String, Number ],
      default: 20
    },

    error: Boolean,

    /**
     * Whether to display the current character count for textarea inputs.
     */
    showCharacterCount: {
      type: Boolean,
      default: false
    },

    /**
     * Sets the focus stroke to the client's brand color instead of the
     * secondary (convenia-green) color.
     */
    brand: Boolean,
  },

  setup (_, ctx) {
    const {
      componentListeners,
    } = useVue3Migration(ctx)

    return {
      componentListeners,
    }
  },

  computed: {
    classes () {
      return [
        'input',
        {
          '-has-icon': this.icon,
          '-has-right-icon': this.rightIcon,
          '-round': this.round,
          '-opaque': this.opaque,
          '-no-hover': this.noHover,
          '-disabled': this.disabled,
          '-textarea': this.textArea,
          '-empty': !(this.$attrs.value || '').length,
          '-is-safari': this.isSafari,
          '-brand': this.brand
        }
      ]
    },

    style () {
      return {
        '--horizontal-padding': `${this.horizontalPadding}px`
      }
    },

    currentLength () {
      return String(
        this.$attrs.value ||
        this.$attrs.modelValue ||
        ''
      ).length
    }
  },

  data () {
    return { notEmpty: false }
  },

  methods: {
    currentMaxLength () {
      return this.$attrs.maxLength || 0
    },

    handleInput (value) {
      this.notEmpty = !!value
    },

    focus () {
      try {
        this.$refs.input.$el.focus()
      } catch (e) { return e }
    }
  },

  mounted () {
    this.$nextTick(() => {
      const { input } = this.$refs
      if (input) this.handleInput(input.$el.value)
    })
  }
}
</script>

<style lang="scss">
$icon-position: 8px;

.c-input-raw {
  position: relative;
  display: flex;
  flex-direction: column;

  & > .left-icon, & > .right-icon {
    box-sizing: content-box;
    position: absolute;
    top: 20px;
    transform: translateY(-50%);
    @include icon-color(text, base-30);
    padding-right: 5px;
  }

  & > .right-icon { right: $icon-position; }
  & > .left-icon { left: $icon-position; }
  & > .character-count {
    align-self: flex-end;
    margin-top: 10px;
  }

  &:not(.-has-under-input-slot) > .input {
    $text-indent: var(--horizontal-padding);
    padding-right: var(--horizontal-padding);
    $text-indent-icon: 40px;
    outline: 0;
    font-size: 14px;
    border: var(--base-border);
    border-radius: 5px;
    color: color-var(text, base-80);
    transition: box-shadow .3s ease;
    -webkit-appearance: none;
    -moz-appearance: none;

    @include convenia-theme() {
      border-color: var(--color-primary-10);
    }

    &::placeholder {
      color: var(--text-30);
    }

    &.-empty {
      @include truncate(100%);
    }

    &:focus:not(.-no-hover) {
      border-color: rgba(color-var(primary, base-rgb), .35);
      @include hover();

      @include convenia-theme() {
        border-color: var(--color-secondary);
        @include hover-shadow('secondary');
      }

      &.-brand {
        border-color: var(--color-brand);
        @include hover-shadow('brand', 50%);
      }
    }

    &.-textarea {
      padding: 10px;
      text-indent: initial !important;
      white-space: pre-wrap;
      overflow-y: auto;
      min-height: 120px;
      max-height: 270px;
    }

    &:not(.-textarea) { height: 40px; text-indent: $text-indent; }

    &.-has-icon { text-indent: $text-indent-icon; }
    // &.-has-icon.-disabled { text-indent: $text-indent-icon + #{var(--base-border-size)}; }
    &.-has-right-icon { padding-right: 40px; }
    &.-round {
      border-radius: 20px;
      border: var(--base-border);
      @include convenia-theme() { border-color: var(--color-primary-10); }
    }
    &.-opaque { background-color: color-var(text, base-05) }
    &.-disabled {
      cursor: default;
      font-family: var(--base-font-family);
      border: 0;
      color: #40486D;
      opacity: 1;
      background-color: color-var(text, base-05);

      @include convenia-theme() {
        color: var(--text-80);
        background-color: var(--color-primary-5);

        &::placeholder { color: var(--text-30); }
      }

      &.-textarea{ border: 1px solid transparent; }
    }

    &::-webkit-inner-spin-button,::-webkit-outer-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
  }

  & > .validation { display: block; }
  & > .jumbo-validation { display: none; }

  &.-validation > .input,
  &.-validation > .input:focus:not(.-no-hover) {
    &:not(.-disabled) {
      border-color: rgba(color-var(negative, base-rgb), .35);
      @include hover(color-var(negative, base-rgb));
    }
  }

  &.-jumbo {
    & > .left-icon { left: 20px; }
    & > .right-icon { right: 20px; }

    & > .input {
      padding-top: 14px;
      text-indent: 0;
      padding-right: 20px;
      padding-left: 20px;

      &.-textarea { padding-top: 27px; }
      &:not(.-textarea) { height: 60px; }
      &::placeholder { color: transparent; }
    }

    & > .label {
      top: 50%;
      transform: translateY(-50%);
      padding: 0 20px;
      font-family: var(--base-font-family);
      font-size: 14px;
      color: color-var(text, base-30);
      pointer-events: none;
      transition: top .3s, font-size .3s, transform .3s;
    }

    &.-textarea > .label { top: 30px; }

    &.-has-icon {
      & > .label { left: 25px; }
      & > .input { padding-left: 44px; }
    }

    &:focus-within > .label,
    &.-not-empty > .label {
      top: 14px;
      transform: translateY(0);
      font-size: 11px;
    }

    &.c-input { margin-top: 0; }

    & > .validation { display: none; }
    & > .jumbo-validation { display: block; }

    &.-validation:not(.-disabled) {
      & > .input {
        border-color: rgba(color-var(negative, base-rgb), .2);
        background: color-var(negative);
        box-shadow: none;
        color: color-var(negative);
        padding-right: 50px;
      }
      & > .input:focus:not(.-no-hover) { @include hover(color-var(negative, base-rgb)); }
      & > .label { color: color-var(negative); }
      & > .left-icon,
      & > .right-icon { @include icon-color(negative); }
    }
  }

  @include xs-mobile {
    &.-jumbo {
      &:not(.-textarea) { height: 50px; }

      & > .label { padding: 0 15px; }

      &:focus-within > .label,
      &.-not-empty > .label { top: 6px; }

      & > .input {
        padding: {
          top: 10px;
          left: 15px;
          right: 15px;
        }
      }
    }
  }

  &.-has-under-input-slot > .input {
    width: 100%;
    display: flex;
    flex-direction: column;
    border: var(--base-border);
    border-radius: 5px;
    $text-indent: var(--horizontal-padding);
    $text-indent-icon: 40px;

    @include convenia-theme() {
      border-color: var(--color-primary-10);
    }

    &, & > .input {
      transition: box-shadow .3s ease;

      &::-webkit-inner-spin-button, &::-webkit-outer-spin-button {
        -webkit-appearance: none;
        margin: 0;
      }
    }

    & > .input {
      padding-right: var(--horizontal-padding);
      font-size: 14px;
      outline: 0;
      color: color-var(text, base-80);
      -webkit-appearance: none;
      -moz-appearance: none;

      &::placeholder {
        color: var(--text-30);
      }
    }

    &.-empty > .input {
      @include truncate(100%);
    }

    &:focus:not(.-no-hover) > .input {
      border-color: rgba(color-var(primary, base-rgb), .35);
      @include hover();

      @include convenia-theme() {
        border-color: var(--color-secondary);
        @include hover-shadow('secondary');
      }
    }

    &.-brand:focus:not(.-no-hover) > .input {
      border-color: var(--color-brand);
      @include hover-shadow('brand', 50%);
    }

    &.-textarea > .input {
      padding: 10px;
      text-indent: initial !important;
      white-space: pre-wrap;
      overflow-y: auto;
      min-height: 120px;
      max-height: 270px;
    }

    &:not(.-textarea) {
      &, & > .input {
        height: 40px;
        text-indent: $text-indent;
      }
    }

    &.-has-icon > .input { text-indent: $text-indent-icon; }
    &.-has-right-icon > .input { padding-right: 40px; }
    &.-round > .input {
      border-radius: 20px;
      border: var(--base-border);
      @include convenia-theme() { border-color: var(--color-primary-10); }
    }
    &.-opaque > .input { background-color: color-var(text, base-05) }
    &.-disabled > .input, &.-disabled > .under-input {
      cursor: default;
      font-family: var(--base-font-family);
      border: 0;
      color: #40486D;
      opacity: 1;
      background-color: color-var(text, base-05);
      pointer-events: none;

      @include convenia-theme() {
        color: var(--text-80);
        background-color: var(--color-primary-5);

        &::placeholder { color: var(--text-30); }
      }

      &.-textarea > .input { border: 1px solid transparent; }
    }
  }
}
</style>
